import { ChangeEvent, useMemo, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { MAX_UPLOAD_SIZE_BYTES, MAX_UPLOAD_SIZE_MB } from '@low6-pickem/ui';
import {
  useDeleteTenantImageMutation,
  useGetTenantImageLibraryQuery,
  useUploadTenantImageMutation,
} from '@/api/tenants/tenantImageLibrary';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Copy, ExternalLink, Loader2, RefreshCcw, Search, Trash2, Upload } from 'lucide-react';

export const Route = createFileRoute('/dashboard/tenant/$tenantCode/image-library')({
  component: TenantImageLibraryRoute,
});

function formatFileSize(bytes: number) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function formatDate(value: string | null) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString();
}

function getDisplayName(blobName: string) {
  const segments = blobName.split('/');
  return segments[segments.length - 1] || blobName;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}

function TenantImageLibraryRoute() {
  const { tenantCode } = Route.useParams();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [searchValue, setSearchValue] = useState('');

  const {
    data: imageLibrary,
    isLoading,
    isError,
    isFetching,
    refetch,
  } = useGetTenantImageLibraryQuery(tenantCode);
  const uploadMutation = useUploadTenantImageMutation(tenantCode);
  const deleteMutation = useDeleteTenantImageMutation(tenantCode);

  // Absent while the first request is in flight; only an explicit false means
  // the instance has no blob storage (CAE-676).
  const storageUnconfigured = imageLibrary?.storageConfigured === false;
  const images = imageLibrary?.images ?? [];
  const filteredImages = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return images;
    return images.filter(image => image.name.toLowerCase().includes(query));
  }, [images, searchValue]);

  const deletingBlobName =
    typeof deleteMutation.variables === 'string' ? deleteMutation.variables : null;

  const onSelectImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    if (file.size > MAX_UPLOAD_SIZE_BYTES) {
      toast.error(`Image file size must be less than ${MAX_UPLOAD_SIZE_MB}MB`);
      return;
    }

    try {
      const imageData = await fileToDataUrl(file);
      const filename = file.name.replace(/\.[^.]+$/, '').trim() || 'tenant-image';
      await uploadMutation.mutateAsync({ filename, imageData });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to upload image');
    }
  };

  const handleDelete = async (blobName: string) => {
    const displayName = getDisplayName(blobName);
    const shouldDelete = window.confirm(`Delete "${displayName}" from the tenant image library?`);
    if (!shouldDelete) return;
    await deleteMutation.mutateAsync(blobName);
  };

  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Image URL copied');
    } catch {
      toast.error('Failed to copy URL');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <h3 className="text-lg font-semibold">Tenant Image Library</h3>
          <p className="text-sm text-muted-foreground">
            Manage images stored in{' '}
            <span className="font-mono">picks-core/tenant-images/{tenantCode}</span>.
          </p>
        </div>

        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onSelectImage}
          />
          {!storageUnconfigured && (
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
            >
              {uploadMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Upload Image
                </>
              )}
            </Button>
          )}

          <Button
            type="button"
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching || uploadMutation.isPending || deleteMutation.isPending}
          >
            <RefreshCcw className={isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by filename or path..."
            className="pl-9"
            value={searchValue}
            onChange={event => setSearchValue(event.target.value)}
          />
        </div>

        <Badge variant="outline">
          {filteredImages.length} image{filteredImages.length === 1 ? '' : 's'}
        </Badge>
      </div>

      {isLoading ? (
        <div className="py-10 text-center text-sm text-muted-foreground">
          Loading tenant images...
        </div>
      ) : isError ? (
        <div className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-700">
          Failed to load tenant image library. Please try refreshing.
        </div>
      ) : storageUnconfigured ? (
        <div className="rounded-md border border-dashed p-10 text-center">
          <div className="text-sm font-medium">Image storage is not configured</div>
          <div className="mt-1 text-xs text-muted-foreground">
            This environment has no Azure Blob Storage connection, so there is no image library to
            show and uploads are unavailable. Set{' '}
            <span className="font-mono">AZURE_STORAGE_CONNECTION_STRING</span> on the backend to
            enable it.
          </div>
        </div>
      ) : filteredImages.length === 0 ? (
        <div className="rounded-md border border-dashed p-10 text-center">
          <div className="text-sm font-medium">
            {images.length === 0 ? 'No images uploaded yet' : 'No images match your search'}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            Upload PNG/JPG/SVG/WebP files up to {MAX_UPLOAD_SIZE_MB}MB.
          </div>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredImages.map(image => {
            const displayName = getDisplayName(image.name);
            const isDeleting = deleteMutation.isPending && deletingBlobName === image.name;

            return (
              <div key={image.name} className="rounded-lg border bg-card p-3 shadow-sm">
                <div className="mb-3 overflow-hidden rounded-md border bg-muted/20">
                  <img
                    src={image.url}
                    alt={displayName}
                    className="h-40 w-full object-contain"
                    loading="lazy"
                  />
                </div>

                <div className="space-y-1">
                  <div className="truncate text-sm font-medium" title={displayName}>
                    {displayName}
                  </div>
                  <div className="text-xs text-muted-foreground">{formatFileSize(image.size)}</div>
                  <div className="text-xs text-muted-foreground">
                    Updated: {formatDate(image.lastModified)}
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleCopyUrl(image.url)}>
                    <Copy className="h-3.5 w-3.5" />
                    Copy URL
                  </Button>

                  <Button size="sm" variant="outline" asChild>
                    <a href={image.url} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-3.5 w-3.5" />
                      View
                    </a>
                  </Button>

                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(image.name)}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                    Remove
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
